/* @flow */

import clsx from 'clsx';
import * as React from 'react';
import { useMemo } from 'react';

import type { NETGEM_API_V8_FEED_ITEM } from '../../libs/netgemLibrary/v8/types/FeedItem';

import { getIntegerPercentage } from '../../helpers/maths/maths';
import { BroadcastStatus } from '../../helpers/ui/location/Format';
import styles from './ProgressBar.module.css';

// Ensures that minimum progress is displayed when a program has been watched for a very short time (5%)
const MIN_PERCENTAGE = 5;

/*
 * ProgressBar component should be called with either {broadcastStatus, item, now} or {progress}, with progress in [0,100]
 */
export type ProgressBarPropType = {
  +allowZeroProgress?: boolean,
  +broadcastStatus?: BroadcastStatus,
  +docked?: boolean,
  +item?: NETGEM_API_V8_FEED_ITEM,
  +now?: number,
  +progress?: number | null,
};

// Return a percentage between 0 and 100
const getProgress = ({ allowZeroProgress, broadcastStatus, item, now, progress }: ProgressBarPropType): number | null => {
  if (typeof progress === 'number') {
    if (progress > 0) {
      // Progress is directly provided by parent component (button for live or catchup tile/card)
      return progress;
    } else if (progress === 0) {
      return allowZeroProgress ? 0 : MIN_PERCENTAGE;
    }
  }

  if (broadcastStatus === undefined || broadcastStatus !== BroadcastStatus.Live || item === undefined || now === undefined) {
    return null;
  }

  // Progress has to be computed from item start and end times (live tile)
  const { endTime, startTime } = item;
  return getIntegerPercentage(now, startTime, endTime);
};

const ProgressBar = ({ allowZeroProgress = true, broadcastStatus, docked = false, item, now, progress }: ProgressBarPropType): React.Node => {
  const progressPct = useMemo(
    () =>
      getProgress({
        allowZeroProgress,
        broadcastStatus,
        item,
        now,
        progress,
      }),
    [allowZeroProgress, broadcastStatus, item, now, progress],
  );

  if (progressPct === null) {
    // No progres to display
    return null;
  }

  return <progress className={clsx(docked && styles.docked)} max={100} role='presentation' value={progressPct} />;
};

export default ProgressBar;
